import React, { FormEvent, useEffect, useState } from "react";
import { analytics } from "../services/analytics";

interface PartnerForm {
  name: string;
  email: string;
  whatsapp: string;
  city: string;
  occupation: string;
  document: string;
  pixKey: string;
  instagram: string;
  notes: string;
  alreadyAttendsLocalBusinesses: string;
  referralType: string;
  volumePerMonth: string;
  termsAccepted: boolean;
}

interface PartnerSignupResponse {
  ok?: boolean;
  link?: string;
  error?: string;
  existing?: boolean;
}

const partnerProfiles = [
  "Social media",
  "Designer",
  "Videomaker",
  "Gestor de tráfego",
  "Produto de conteúdo",
  "Vendedor autônomo",
  "Consultor",
  "Agência",
  "Prestador de serviço local",
  "Profissionais liberais",
  "Freelancer digital",
  "Dono de comercial local"
];

const partnerSteps = [
  ["1", "Cadastro", "Voce informa seus dados e aceita as regras do programa."],
  ["2", "Link unico", "O sistema gera seu link exclusivo para enviar aos indicados."],
  ["3", "Indicacao", "O cliente faz o diagnostico gratuito usando seu link."],
  ["4", "Treinamento", "Te entregamos materiais e contatos para prospecção de clientes"],
  ["5", "Entrega", "A Vitrine conduz venda, briefing, criacao, publicacao e suporte."],
  ["6", "Comissao", "Clientes pagos geram comissao conforme as regras vigentes."]
];

const customerBenefits = [
  ["Diagnostico gratuito", "O cliente entende onde a presenca digital pode melhorar antes de contratar."],
  ["Site profissional", "Entrega pensada para pequenos negocios locais, WhatsApp, mapa e clareza comercial."],
  ["Atendimento humano", "Nao e construtor faca voce mesmo. A Vitrine conduz a execucao com o cliente."],
  ["Planos acessiveis", "Opcao mensal gerenciada e opcao de pagamento unico, conforme o perfil do negocio."]
];

const commissionPlans = [
  {
    name: "Lifetime",
    tag: "Pagamento unico",
    price: "R$591",
    period: "por venda",
    description: "30% sobre o plano Lifetime de R$1.970, pago em cota unica apos confirmacao do pagamento.",
    examples: ["1 venda = R$591", "3 vendas = R$1.773", "5 vendas = R$2.955"]
  },
  {
    name: "Pro",
    tag: "Assinatura",
    price: "Ate R$295,50",
    period: "em 5 meses",
    description: "30% da mensalidade de R$197, por ate 5 meses, enquanto o cliente indicado estiver pagando.",
    examples: ["1 cliente = R$295,50 max", "5 clientes = R$1.477,50 max", "10 clientes = R$2.955 max"]
  }
];

export function App() {
  useEffect(() => {
    analytics.init();
    analytics.trackPageView(window.location.pathname, document.title);
    analytics.initClickDelegation();
  }, []);

  const [form, setForm] = useState<PartnerForm>({
    name: "",
    email: "",
    whatsapp: "",
    city: "",
    occupation: "",
    document: "",
    pixKey: "",
    instagram: "",
    notes: "",
    alreadyAttendsLocalBusinesses: "",
    referralType: "",
    volumePerMonth: "",
    termsAccepted: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [partnerLink, setPartnerLink] = useState("");
  const [existingSignup, setExistingSignup] = useState(false);
  const [copied, setCopied] = useState(false);

  function goForm() {
    document.querySelector("#partner-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function submitPartner(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    if (!form.termsAccepted) {
      setError("Voce precisa aceitar os termos do programa.");
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch("/api/partner-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          sourcePath: window.location.pathname
        })
      });
      const payload = (await response.json().catch(() => ({}))) as PartnerSignupResponse;
      if (!response.ok || !payload.ok) throw new Error(payload.error || "Nao foi possivel enviar o cadastro.");

      analytics.trackEvent(payload.existing ? "partner_signup_existing" : "partner_signup_success", {
        partner_occupation: form.occupation
      });
      setExistingSignup(Boolean(payload.existing));
      setPartnerLink(payload.link || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao enviar cadastro.");
    } finally {
      setSubmitting(false);
    }
  }

  function setValue<K extends keyof PartnerForm>(key: K, value: PartnerForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(partnerLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setError("Nao foi possivel copiar automaticamente. Selecione o link e copie manualmente.");
    }
  }

  return (
    <div className="site">
      <Header onCta={goForm} />

      <main>
        <section className="hero" id="topo">
          <div className="container hero-grid">
            <div>
              <span className="eyebrow">Programa de Parceiros</span>
              <h1>Indique sites profissionais para negocios locais e ganhe por cliente fechado.</h1>
              <p className="hero-copy">
                Voce indica. A Vitrine conduz diagnostico, venda, briefing, criacao, publicacao e suporte. Seu link identifica as oportunidades que vierem da sua rede.
              </p>
              <div className="chips">
                <span className="chip">Link exclusivo</span>
                <span className="chip">Comissao via Pix</span>
                <span className="chip">Sem precisar criar sites</span>
              </div>
              <div className="hero-actions">
                <button className="button button-primary" onClick={goForm}>
                  Quero ser parceiro
                </button>
                <a className="button button-secondary" href="#como-funciona">
                  Como funciona
                </a>
              </div>
            </div>
            <PartnerShowcase />
          </div>
        </section>

        <section className="section section-cream">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">Para quem é</span>
              <h2 className="section-title">Profissionais que ja conversam com pequenos negocios.</h2>
            </div>
            <div className="included-grid">
              {partnerProfiles.map((item) => (
                <div className="included-item" key={item}>
                  <span className="check">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="como-funciona" className="section">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">Como funciona</span>
              <h2 className="section-title">Um fluxo simples, rastreavel e operacional.</h2>
              <p className="section-copy">
                O parceiro fica com a indicacao; a Vitrine fica responsavel por transformar interesse em projeto vendido e entregue.
              </p>
            </div>
            <div className="steps-grid">
              {partnerSteps.map(([number, title, text]) => (
                <article className="step" key={title}>
                  <span className="step-num">{number}</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-dark">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">O cliente indicado</span>
              <h2 className="section-title">O que o negocio recebe da Vitrine.</h2>
            </div>
            <div className="solution-grid">
              {customerBenefits.map(([title, text]) => (
                <article className="solution-card" key={title}>
                  <span className="icon-box">✓</span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="comissao" className="section section-cream">
          <div className="container">
            <div className="section-head center">
              <span className="section-label">Comissao</span>
              <h2 className="section-title">Quanto voce pode ganhar.</h2>
              <p className="section-copy">
                Valores ilustrativos, calculados com base nas comissoes atuais. Nao ha garantia de renda, fechamento ou volume de vendas.
              </p>
            </div>
            <div className="plans-grid">
              {commissionPlans.map((plan) => (
                <article className="plan" key={plan.name}>
                  <div className="plan-top">
                    <h3 className="plan-name">{plan.name}</h3>
                    <span className="plan-tag">{plan.tag}</span>
                  </div>
                  <div className="plan-price">
                    {plan.price}
                    <span className="plan-period">{plan.period}</span>
                  </div>
                  <p className="plan-desc">{plan.description}</p>
                  <ul>
                    {plan.examples.map((example) => (
                      <li className="check" key={example}>
                        {example}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <p className="fineprint">
              O pagamento depende da contratacao, confirmacao do pagamento do cliente indicado e regras vigentes do programa.
            </p>
          </div>
        </section>

        <section className="section checkout-section partner-form-section" id="partner-form">
          <div className="container checkout-grid">
            <div>
              <span className="section-label">Cadastro</span>
              <h2 className="section-title">Cadastre-se para gerar seu link de parceiro.</h2>
              <p className="section-copy">
                Estes dados identificam o parceiro, viabilizam contato operacional e permitem pagamento de comissao quando houver venda confirmada.
              </p>
              <div className="diagnostic-benefits">
                <span>
                  <CheckIcon /> Cadastro unico por e-mail ou CPF/CNPJ.
                </span>
                <span>
                  <CheckIcon /> Link direciona para o diagnostico gratuito.
                </span>
                <span>
                  <CheckIcon /> Comissao paga via Pix conforme regra do programa.
                </span>
              </div>
            </div>

            {partnerLink ? (
              <div className="form-card partner-success-card">
                <span className="section-label">{existingSignup ? "Cadastro existente" : "Cadastro recebido"}</span>
                <h3>{existingSignup ? "Encontramos seu cadastro." : "Seu link esta pronto."}</h3>
                <p>
                  {existingSignup
                    ? "Este e-mail ou documento ja estava cadastrado. Use o link abaixo para continuar indicando."
                    : "Use este link para indicar clientes ao diagnostico gratuito da Vitrine Website."}
                </p>
                <div className="partner-link-box">
                  <a href={partnerLink} target="_blank" rel="noreferrer">
                    {partnerLink}
                  </a>
                </div>
                <button className="button button-primary" onClick={copyLink}>
                  {copied ? "Link copiado" : "Copiar link"}
                </button>
              </div>
            ) : (
              <form className="form-card" onSubmit={submitPartner}>
                <div className="form-grid">
                  <label className="form-field full">
                    <span>Nome completo *</span>
                    <input type="text" required value={form.name} onChange={(e) => setValue("name", e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>E-mail *</span>
                    <input type="email" required value={form.email} onChange={(e) => setValue("email", e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>WhatsApp *</span>
                    <input type="tel" required placeholder="(11) 99999-9999" value={form.whatsapp} onChange={(e) => setValue("whatsapp", e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>Cidade/UF *</span>
                    <input type="text" required value={form.city} onChange={(e) => setValue("city", e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>Ocupacao *</span>
                    <select required value={form.occupation} onChange={(e) => setValue("occupation", e.target.value)}>
                      <option value="">Selecione</option>
                      <option value="Social media">Social media</option>
                      <option value="Designer">Designer</option>
                      <option value="Fotografo">Fotografo</option>
                      <option value="Gestor de trafego">Gestor de trafego</option>
                      <option value="Vendedor autonomo">Vendedor autonomo</option>
                      <option value="Consultor">Consultor</option>
                      <option value="Contador">Contador</option>
                      <option value="Agencia">Agencia</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </label>
                  <label className="form-field">
                    <span>CPF ou CNPJ *</span>
                    <input type="text" required value={form.document} onChange={(e) => setValue("document", e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>Chave Pix para comissao *</span>
                    <input type="text" required value={form.pixKey} onChange={(e) => setValue("pixKey", e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>Voce ja atende pequenos negocios? *</span>
                    <select required value={form.alreadyAttendsLocalBusinesses} onChange={(e) => setValue("alreadyAttendsLocalBusinesses", e.target.value)}>
                      <option value="">Selecione</option>
                      <option value="Sim">Sim</option>
                      <option value="Nao">Nao</option>
                      <option value="As vezes">As vezes</option>
                    </select>
                  </label>
                  <label className="form-field">
                    <span>Negocios que consegue indicar</span>
                    <input type="text" placeholder="Ex: barbearias, oficinas, clinicas" value={form.referralType} onChange={(e) => setValue("referralType", e.target.value)} />
                  </label>
                  <label className="form-field">
                    <span>Volume que pode abordar este mes *</span>
                    <select required value={form.volumePerMonth} onChange={(e) => setValue("volumePerMonth", e.target.value)}>
                      <option value="">Selecione</option>
                      <option value="1 a 5">1 a 5</option>
                      <option value="6 a 10">6 a 10</option>
                      <option value="11 a 20">11 a 20</option>
                      <option value="Mais de 20">Mais de 20</option>
                      <option value="Ainda nao sei">Ainda nao sei</option>
                    </select>
                  </label>
                  <label className="form-field">
                    <span>Instagram</span>
                    <input type="text" value={form.instagram} onChange={(e) => setValue("instagram", e.target.value)} />
                  </label>
                  <label className="form-field full">
                    <span>Observacoes</span>
                    <textarea rows={3} value={form.notes} onChange={(e) => setValue("notes", e.target.value)} />
                  </label>
                </div>

                <label className="diagnostic-consent partner-consent">
                  <input
                    type="checkbox"
                    required
                    checked={form.termsAccepted}
                    onChange={(e) => setValue("termsAccepted", e.target.checked)}
                  />
                  <span>
                    Li e aceito as regras do <a href="/politicas" target="_blank" rel="noreferrer">Programa de Parceiros Vitrine Website</a>, incluindo parceria autonoma, ausencia de vinculo CLT, comissao apenas sobre clientes pagos e ausencia de garantia de ganhos.
                  </span>
                </label>

                {error && <p className="form-error">{error}</p>}

                <button className="button button-primary" type="submit" disabled={submitting}>
                  {submitting ? "Enviando..." : "Cadastrar e gerar link"}
                </button>
                <p className="form-note">Campos marcados com * sao obrigatorios.</p>
              </form>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Header({ onCta }: { onCta: () => void }) {
  return (
    <header className="header">
      <div className="container header-inner">
        <a className="brand" href="/">
          <BrandMark />
          <span className="brand-name">
            Vitrine<span>Website</span>
          </span>
        </a>
        <nav className="nav">
          <a href="#como-funciona">Como funciona</a>
          <a href="#comissao">Comissao</a>
          <button className="button button-primary" onClick={onCta}>
            Quero ser parceiro
          </button>
        </nav>
      </div>
    </header>
  );
}

function PartnerShowcase() {
  return (
    <div className="mock-wrap phone-showcase" aria-hidden="true">
      <div className="floating-badge badge-top badge-seo">
        <span className="badge-icon badge-icon-soft">
          <TrendIcon />
        </span>
        <div>
          <span>Indicacao</span>
          Link rastreavel
        </div>
      </div>
      <div className="floating-badge badge-bottom badge-performance">
        <span className="badge-icon badge-icon-warm">
          <BoltIcon />
        </span>
        <div>
          <span>Entrega</span>
          Site profissional
        </div>
      </div>

      <div className="phone iphone-mock">
        <div className="phone-screen barber-screen">
          <div className="phone-status">
            <span>9:41</span>
            <span>5G</span>
          </div>
          <div className="barber-mobile-hero">
            <div className="barber-mobile-nav">
              <strong>Cliente indicado</strong>
              <span>Diagnostico</span>
            </div>
            <div className="barber-photo">
              <img src="/assets/barber-shop-premium.jpg" alt="" />
            </div>
            <span className="mock-kicker">Negocio local</span>
            <div className="mock-title">Site pronto para receber contatos.</div>
            <div className="mock-wa">
              <WhatsAppIcon />
              Chamar no WhatsApp
            </div>
          </div>
          <div className="mock-body">
            <div className="service-pill">
              <div>
                <small>Origem</small>
                <strong>Parceiro Vitrine</strong>
              </div>
              <span>ref</span>
            </div>
            <div className="mock-cards">
              <div className="mock-card">
                <small>Prazo</small>
                <strong>48h</strong>
              </div>
              <div className="mock-card">
                <small>Canal</small>
                <strong>WhatsApp</strong>
              </div>
            </div>
            <div className="mock-map">
              Diagnostico gratuito
              <small>Identifica oportunidades antes da venda</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-inner">
        <div className="footer-brand">
          <a className="brand" href="/">
            <BrandMark />
            <span className="brand-name" style={{ color: "#fff" }}>
              Vitrine<span>Website</span>
            </span>
          </a>
          <p className="footer-tagline">Site profissional e diagnostico de presenca digital para negocios locais.</p>
        </div>
        <div className="footer-links">
          <a href="/diagnostico">Diagnostico gratuito</a>
          <a href="/politicas">Politicas e privacidade</a>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="container">
          © 2026 Vitrine Website · by{" "}
          <a href="https://caporal.studio" style={{ textDecoration: "none", color: "#fff", fontWeight: 600 }}>
            Caporal Studio
          </a>
        </div>
      </div>
    </footer>
  );
}

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden="true">
      <StorefrontIcon />
    </span>
  );
}

function StorefrontIcon() {
  return (
    <svg className="brand-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 10.5V19h14v-8.5" />
      <path d="M4 10.5h16L18.8 5H5.2L4 10.5Z" />
      <path d="M4 10.5c0 1.2 1 2.1 2.2 2.1s2.2-.9 2.2-2.1c0 1.2 1 2.1 2.2 2.1s2.2-.9 2.2-2.1c0 1.2 1 2.1 2.2 2.1s2.2-.9 2.2-2.1c0 1.2 1 2.1 2.2 2.1" />
      <path d="M9 19v-4h6v4" />
    </svg>
  );
}

function TrendIcon() {
  return (
    <svg className="badge-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m4 16 5-5 4 4 7-8" />
      <path d="M14 7h6v6" />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg className="badge-svg filled" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 2 4 14h7l-1 8 10-13h-7l0-7Z" />
    </svg>
  );
}

function WhatsAppIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5.4 19.4 6.3 16A7.2 7.2 0 1 1 9 18.4l-3.6 1Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.9"
      />
      <path
        d="M9.4 8.2c.2-.3.4-.4.7-.4h.5c.2 0 .4.1.5.3l.6 1.4c.1.2.1.4-.1.6l-.4.5c.5.9 1.2 1.6 2.1 2.1l.5-.4c.2-.2.4-.2.6-.1l1.4.6c.2.1.3.3.3.5v.5c0 .3-.1.5-.4.7-.5.3-1.1.4-1.7.2-2.5-.7-4.5-2.7-5.2-5.2-.2-.6-.1-1.2.2-1.7Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="badge-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m5 12 4 4L19 6" />
    </svg>
  );
}
